/**
 * Messages utility functions
 *
 * Centralizes the creation and mutation of message objects used by the chat UI
 * so components can remain lean. All functions here are pure and return new
 * objects/arrays (do not mutate arguments).
 */

// Counter to ensure unique IDs even when created in the same millisecond
let messageIdCounter = 0;
let lastTimestamp = 0;

/**
 * Generate a unique message ID using timestamp + counter
 * Ensures uniqueness even when multiple messages are created in the same millisecond
 * @returns {string}
 */
const generateMessageId = () => {
  const now = Date.now();
  
  // Reset counter if we're in a new millisecond
  if (now !== lastTimestamp) {
    messageIdCounter = 0;
    lastTimestamp = now;
  } else {
    messageIdCounter += 1;
  }
  
  // Use string concatenation to avoid potential overflow issues
  // Format: timestamp-counter ensures uniqueness
  return `${now}-${messageIdCounter}`;
};

/**
 * @typedef {('sent'|'received')} MessageType
 * @typedef {('text'|'project-details'|'metrics'|'diagram'|'log'|'choice'|'task-progress')} ContentType
 */

/**
 * @typedef {Object} ReasoningStep
 * @property {string} title - Title of the reasoning step
 * @property {string} [description] - Description of what's happening
 * @property {Object} [functionCall] - Function call details
 * @property {string} functionCall.name - Name of the function being called
 * @property {Object} [functionCall.args] - Function arguments
 * @property {string|Object} [result] - Result from the function call
 * @property {string} [reasoning] - Reasoning text for this step
 * @property {number} [duration] - Duration in milliseconds
 * @property {number} [startTime] - Start timestamp
 * @property {number} [endTime] - End timestamp
 */

/**
 * Core message shape used by the chat UI.
 *
 * @typedef {Object} ChatMessage
 * @property {string} id - Unique identifier for stable rendering
 * @property {string} text - Message content (markdown supported on render)
 * @property {MessageType} type - 'sent' for user, 'received' for assistant/system
 * @property {boolean} [isIntermediate] - Marks streaming/in-progress assistant chunks
 * @property {boolean} [isFunctionCall] - Marks tool/function call status messages
 * @property {string} [functionName] - Function name when isFunctionCall is true
 * @property {string|number} [functionId] - Function id (matches model/tool id)
 * @property {ContentType} [contentType] - Type of structured content to render
 * @property {Object} [structuredContent] - Structured data for inline UI components
 * @property {ReasoningStep[]} [reasoningSteps] - Array of reasoning steps for this message
 * @property {number} [currentReasoningStep] - Index of the current active reasoning step
 */

/**
 * Create a user-authored message.
 * @param {string} text - Input text from the user
 * @returns {ChatMessage}
 */
export const createUserMessage = (text) => ({
    id: generateMessageId(),
    text,
    type: 'sent'
});

/**
 * Create an assistant-styled error message.
 * @param {string} errorText - Error details to surface to the user
 * @returns {ChatMessage}
 */
export const createErrorMessage = (errorText) => ({
    id: generateMessageId(),
    text: `Error: ${errorText}`,
    type: 'received'
});

/**
 * Create an assistant-styled info/status message (without "Error:" prefix).
 * @param {string} infoText - Information or status to show to the user
 * @returns {ChatMessage}
 */
export const createInfoMessage = (infoText) => ({
    id: generateMessageId(),
    text: infoText,
    type: 'received'
});

/**
 * Create a status message representing a function/tool invocation started by the model.
 * The caller can later mark it complete with updateFunctionCallMessage().
 *
 * @param {{ id?: string|number, name: string, args?: Object }} functionCall - Parsed function/tool call from the stream
 * @returns {ChatMessage}
 */
export const createFunctionCallMessage = (functionCall) => {
    const functionId = functionCall.id || functionCall.name;
    return {
        id: `function-${functionId}`,
        text: `🛠️ Using **${functionCall.name}**...`,
        type: 'received',
        isFunctionCall: true,
        functionName: functionCall.name,
        functionId: functionId,
        functionArgs: functionCall.args
    };
};

/**
 * Mark a previously created function call message as complete (adds a ✅ suffix).
 * Matches by `id` (or `name`) and preserves message order.
 *
 * @param {ChatMessage[]} messages - Current messages array
 * @param {{ id?: string|number, name: string }} functionResponse - Tool completion payload
 * @returns {ChatMessage[]} New array with the matching function message updated
 */
export const updateFunctionCallMessage = (messages, functionResponse) => {
    const functionId = functionResponse.id || functionResponse.name;
    const functionName = functionResponse.name;
    
    return messages.map(msg => {
        if (msg.id === `function-${functionId}` && msg.isFunctionCall) {
            return {
                ...msg,
                text: `🛠️ Using **${functionName}**... ✅`
            };
        }
        return msg;
    });
};

/**
 * Detect whether a chunk of text ends inside an unclosed code block.
 * Useful for renderers that need to delay markdown rendering until the closing
 * fence arrives to avoid content appearing outside the code block during streaming.
 *
 * @param {string} text - Accumulated markdown text
 * @returns {boolean} True if still within an open code block
 */
export const isInsideIncompleteCodeBlock = (text) => {
    // Split text into lines to properly parse code blocks
    const lines = text.split('\n');
    let inCodeBlock = false;
    
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        
        // Check for opening code fence (``` followed by optional language)
        if (line.startsWith('```')) {
            if (inCodeBlock) {
                // Closing fence
                inCodeBlock = false;
            } else {
                // Opening fence
                inCodeBlock = true;
            }
        }
    }
    
    return inCodeBlock;
};

/**
 * Detect whether a chunk of text ends inside an unclosed ```mermaid code block.
 * Useful for renderers that need to delay markdown rendering until the closing
 * fence arrives to avoid flashing chart errors.
 *
 * @param {string} text - Accumulated markdown text
 * @returns {boolean} True if still within an open mermaid block
 */
export const isInsideIncompleteMermaidBlock = (text) => {
    // Split text into lines to properly parse code blocks
    const lines = text.split('\n');
    let inMermaidBlock = false;
    
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        
        if (line.startsWith('```mermaid')) {
            inMermaidBlock = true;
        } else if (line === '```' && inMermaidBlock) {
            inMermaidBlock = false;
        }
    }
    
    return inMermaidBlock;
};

/**
 * Detect whether text ends with an incomplete markdown structure during streaming.
 * This prevents rendering partial content that can cause poor formatting.
 * 
 * We should wait for more content if:
 * - Text doesn't end with a natural boundary (double newline, complete sentence)
 * - We're inside an unclosed markdown structure
 * - Last line is a partial list item
 * 
 * @param {string} text - Accumulated markdown text
 * @returns {boolean} True if we should wait for more content before rendering
 */
export const isInsideIncompleteListItem = (text) => {
    if (!text) return false;
    
    // If text ends with double newline, it's a complete paragraph/section
    if (text.endsWith('\n\n')) {
        return false;
    }
    
    const lines = text.split('\n');
    if (lines.length === 0) return false;
    
    // Get the last non-empty line
    let lastLine = '';
    for (let i = lines.length - 1; i >= 0; i--) {
        if (lines[i].trim()) {
            lastLine = lines[i];
            break;
        }
    }
    
    const trimmedLastLine = lastLine.trim();
    if (!trimmedLastLine) return false;
    
    // Check if we're inside a list structure
    // List item patterns: "- ", "* ", "+ ", "•", "1. ", "2. ", etc.
    const listItemStart = /^[\s]*[-*+•✓][\s]+|^[\s]*\d+\.[\s]+/;
    const isListItem = listItemStart.test(lastLine);
    
    if (isListItem) {
        // List item is complete if it ends with proper punctuation or is long enough
        const endsWithPunctuation = /[.!?:)]$/.test(trimmedLastLine);
        const isLongEnough = trimmedLastLine.length > 40; // More generous threshold
        
        // If it doesn't end with punctuation and isn't long, wait for more
        if (!endsWithPunctuation && !isLongEnough) {
            return true;
        }
    }
    
    // Check if we're in the middle of a bold/italic/code structure
    // Count unclosed markers (simple approach - count all occurrences)
    const boldCount = (text.match(/\*\*/g) || []).length;
    const inlineCodeCount = (text.match(/(?<!\\)`(?!``)/g) || []).length;
    
    // If odd number of markers, we're inside an unclosed structure
    if (boldCount % 2 !== 0 || inlineCodeCount % 2 !== 0) {
        return true;
    }
    
    // Check if last line looks like it's mid-sentence (ends with partial word or no punctuation)
    // But allow rendering if it's not a list item and reasonably long
    if (!isListItem && trimmedLastLine.length > 50) {
        return false; // Long enough non-list content, allow rendering
    }
    
    // If text doesn't end with sentence-ending punctuation or paragraph break,
    // and it's short, wait for more content
    const endsWithSentence = /[.!?]\s*$/.test(text) || text.endsWith('\n\n');
    if (!endsWithSentence && text.length < 100) {
        return true;
    }
    
    return false;
};

/**
 * Clean image data markers from message text for display.
 * Removes [IMAGE_DATA]...[/IMAGE_DATA] tags but keeps the rest of the message.
 * 
 * @param {string} text - Message text that may contain image data markers
 * @returns {string} Cleaned text without image data markers
 */
export const cleanImageDataMarkers = (text) => {
    if (!text) return text;
    
    // Remove [IMAGE_DATA]base64data[/IMAGE_DATA] markers
    // This prevents the huge base64 string from being displayed in the chat
    return text.replace(/\[IMAGE_DATA\]data:image\/[^[]+\[\/IMAGE_DATA\]/g, '🖼️ [Image generated]');
};

/**
 * Normalize streamed text so that single newlines between non-blank lines
 * are promoted to double newlines (paragraph breaks), which markdown renderers
 * need to create actual <p> separations.  Also ensures that a sentence ending
 * in punctuation followed immediately (no newline) by a capital letter gets a
 * paragraph break injected, which handles models that output reasoning steps
 * without any line separators at all.
 *
 * @param {string} text - Raw accumulated text from the stream
 * @returns {string} Text with normalised paragraph breaks
 */
export const normalizeStreamedText = (text) => {
    if (!text) return text;

    // 1. Inject paragraph break where a sentence ends ('.', '!', '?', ':')
    //    and is immediately followed by an uppercase letter or digit with no
    //    whitespace in between (e.g. "...requirements.I'll help...")
    let normalized = text.replace(/([.!?:])([A-Z\u00C0-\u00DC])/g, '$1\n\n$2');

    // 2. Promote single newlines between non-blank content lines to double
    //    newlines so markdown renders them as separate paragraphs.
    //    Do NOT touch already-doubled newlines or newlines adjacent to table rows
    //    (lines that start/end with |) — promoting those would break GFM tables.
    normalized = normalized.replace(/([^\n])\n([^\n])/g, (_, before, after) => {
        if (before === '|' || after === '|') return `${before}\n${after}`;
        return `${before}\n\n${after}`;
    });

    // 3. Remove consecutive duplicate paragraphs produced when the model repeats
    //    a sentence or section verbatim (e.g. intro paragraph sent twice).
    const paragraphs = normalized.split(/\n{2,}/);
    const deduplicated = paragraphs.filter((para, i) =>
        i === 0 || para.trim() !== paragraphs[i - 1].trim()
    );
    normalized = deduplicated.join('\n\n');

    return normalized;
};

/**
 * Update or create an assistant streaming message.
 * If the last message is an intermediate assistant message, update its text.
 * Otherwise, append a new assistant message. The caller controls whether the
 * message remains intermediate or becomes final via isIntermediate flag.
 * Also detects and parses structured content markers.
 *
 * @param {ChatMessage[]} messages - Current messages array
 * @param {string} accumulatedText - Current accumulated partial or final text
 * @param {boolean} [isIntermediate=true] - True for streaming; false to finalize
 * @returns {ChatMessage[]} New messages array reflecting the change
 */
export const updateOrCreateIntermediateMessage = (messages, accumulatedText, isIntermediate = true) => {
    const lastMessage = messages[messages.length - 1];
    
    // Clean the text to remove image data markers for display, then normalize paragraph breaks
    let cleanedText = normalizeStreamedText(cleanImageDataMarkers(accumulatedText));
    
    // Note: Double newlines are intentional markdown formatting (paragraphs, lists, etc.)
    
    // Parse structured content if present
    const structuredData = parseStructuredContent(accumulatedText);
    
    // Update the last message if it's a received message (intermediate OR already finalised).
    // Some models (e.g. Ollama/Qwen) emit partial=false for every token, which would cause
    // the first token to finalise the message (isIntermediate→false) and all subsequent
    // tokens to create new bubbles.  By also accepting the last received non-function-call
    // message we keep everything in a single bubble.
    if (lastMessage && lastMessage.type === 'received' && !lastMessage.isFunctionCall &&
        (lastMessage.isIntermediate || lastMessage.isStreaming)) {
        // Update existing intermediate/streaming message with accumulated text
        const updatedMessage = {
            ...lastMessage,
            text: structuredData ? structuredData.cleanText : cleanedText,
            isIntermediate: isIntermediate,
            // Keep isStreaming flag until the caller explicitly finalises (isIntermediate=false)
            isStreaming: isIntermediate ? lastMessage.isStreaming : false
        };
        
        // Add structured content if detected
        if (structuredData) {
            updatedMessage.contentType = structuredData.contentType;
            updatedMessage.structuredContent = structuredData.structuredContent;
            updatedMessage.rawStructuredText = structuredData.rawStructuredText;
        }
        
        return messages.slice(0, -1).concat([updatedMessage]);
    } else {
        // Create new message
        const newMessage = {
            id: generateMessageId(),
            text: structuredData ? structuredData.cleanText : cleanedText,
            type: 'received'
        };
        
        // Only add isIntermediate property if it's true
        if (isIntermediate) {
            newMessage.isIntermediate = true;
        }
        
        // Add structured content if detected
        if (structuredData) {
            newMessage.contentType = structuredData.contentType;
            newMessage.structuredContent = structuredData.structuredContent;
            newMessage.rawStructuredText = structuredData.rawStructuredText;
        }
        
        return [...messages, newMessage];
    }
};

/**
 * Append a message to the list (immutable).
 * @param {ChatMessage[]} messages - Current messages array
 * @param {ChatMessage} message - Message to add
 * @returns {ChatMessage[]} New array with the message appended
 */
export const addMessage = (messages, message) => {
    return [...messages, message];
};

/**
 * Add a reasoning step to the current streaming message.
 * @param {ChatMessage[]} messages - Current messages array
 * @param {ReasoningStep} step - Reasoning step to add
 * @returns {ChatMessage[]} New array with the reasoning step added
 */
export const addReasoningStep = (messages, step) => {
    if (messages.length === 0) return messages;
    
    const lastMessage = messages[messages.length - 1];
    
    // Only add reasoning steps to assistant messages
    if (lastMessage.type !== 'received' || lastMessage.isFunctionCall) {
        return messages;
    }
    
    const reasoningSteps = lastMessage.reasoningSteps || [];
    const updatedMessage = {
        ...lastMessage,
        reasoningSteps: [...reasoningSteps, {...step, startTime: Date.now()}],
        currentReasoningStep: reasoningSteps.length
    };
    
    return messages.slice(0, -1).concat([updatedMessage]);
};

/**
 * Update the current reasoning step with completion data.
 * @param {ChatMessage[]} messages - Current messages array
 * @param {Partial<ReasoningStep>} updates - Updates to apply to the current step
 * @returns {ChatMessage[]} New array with the reasoning step updated
 */
export const updateCurrentReasoningStep = (messages, updates) => {
    if (messages.length === 0) return messages;
    
    const lastMessage = messages[messages.length - 1];
    
    if (lastMessage.type !== 'received' || !lastMessage.reasoningSteps || lastMessage.reasoningSteps.length === 0) {
        return messages;
    }
    
    const reasoningSteps = [...lastMessage.reasoningSteps];
    const currentIndex = lastMessage.currentReasoningStep ?? reasoningSteps.length - 1;
    
    if (currentIndex < 0 || currentIndex >= reasoningSteps.length) {
        return messages;
    }
    
    const currentStep = reasoningSteps[currentIndex];
    const endTime = Date.now();
    const duration = currentStep.startTime ? endTime - currentStep.startTime : undefined;
    
    reasoningSteps[currentIndex] = {
        ...currentStep,
        ...updates,
        endTime,
        duration
    };
    
    const updatedMessage = {
        ...lastMessage,
        reasoningSteps
    };
    
    return messages.slice(0, -1).concat([updatedMessage]);
};

/**
 * Clear reasoning steps from the last message (when response is complete).
 * @param {ChatMessage[]} messages - Current messages array
 * @returns {ChatMessage[]} New array with reasoning steps cleared
 */
export const clearReasoningSteps = (messages) => {
    if (messages.length === 0) return messages;
    
    const lastMessage = messages[messages.length - 1];
    
    if (lastMessage.type !== 'received') {
        return messages;
    }
    
    const updatedMessage = {
        ...lastMessage,
        reasoningSteps: undefined,
        currentReasoningStep: undefined
    };
    
    return messages.slice(0, -1).concat([updatedMessage]);
};

/**
 * Create a structured content message with inline UI components.
 * @param {string} contentType - Type of inline UI ('project-details', 'metrics', 'diagram', 'log', 'choice', 'task-progress')
 * @param {Object} structuredContent - Data for the inline UI component
 * @param {string} [fallbackText] - Optional markdown text to display alongside or as fallback
 * @returns {ChatMessage}
 */
export const createStructuredMessage = (contentType, structuredContent, fallbackText = '') => ({
    id: generateMessageId(),
    text: fallbackText,
    type: 'received',
    contentType: contentType,
    structuredContent: structuredContent
});

/**
 * Detect and parse structured content markers from SSE text response.
 * Looks for special markers like [STRUCTURED:type]...data...[/STRUCTURED]
 * Returns object with contentType and structuredContent if found, null otherwise.
 * Can handle multiple structured blocks in a single message.
 * 
 * @param {string} text - Text that may contain structured content markers
 * @returns {{contentType: string, structuredContent: Object|Array, cleanText: string, rawStructuredText: string} | null}
 */
export const parseStructuredContent = (text) => {
    if (!text) return null;
    
    // Pattern: [STRUCTURED:type]...JSON data...[/STRUCTURED]
    // More flexible pattern that handles whitespace and single-line JSON
    // Also handles common typos like [/STRUCTUREED]
    const structuredPattern = /\[STRUCTURED:([a-z-]+)\]\s*([\s\S]*?)\s*\[\/STRUCTURED*\]/gi;
    const matches = [...text.matchAll(structuredPattern)];
    
    // Also check for incomplete structured blocks (streaming in progress)
    const incompletePattern = /\[STRUCTURED:([a-z-]+)\](?![\s\S]*?\[\/STRUCTURED\])/i;
    const hasIncompleteBlock = incompletePattern.test(text);
    
    // If there's an incomplete block, clean the text but don't try to parse
    if (hasIncompleteBlock && matches.length === 0) {
        // Remove the incomplete structured content from display
        const cleanText = text.replace(/\[STRUCTURED:[a-z-]+\][\s\S]*$/i, '').trim();
        return {
            contentType: null,
            structuredContent: null,
            cleanText,
            rawStructuredText: ''
        };
    }
    
    if (matches.length === 0) return null;
    
    // Store the original structured text for error fallback
    let rawStructuredText = '';
    for (const match of matches) {
        rawStructuredText += match[0] + '\n\n';
    }
    rawStructuredText = rawStructuredText.trim();
    
    try {
        // If there's only one structured block, return it directly (backward compatible)
        if (matches.length === 1) {
            const contentType = matches[0][1];
            const jsonData = matches[0][2].trim();
            
            try {
                const structuredContent = JSON.parse(jsonData);
                
                // Remove the structured marker from text
                const cleanText = text.replace(structuredPattern, '').trim();
                
                return {
                    contentType,
                    structuredContent,
                    cleanText,
                    rawStructuredText
                };
            } catch (parseError) {
                /* eslint-disable no-console */
                console.error('Failed to parse single structured block:', {
                    contentType, 
                    error: parseError.message, 
                    position: parseError.message.match(/position (\d+)/)?.[1],
                    jsonData: jsonData.substring(0, 150)
                });
                /* eslint-enable no-console */
                // Return null so the content displays as regular text
                return null;
            }
        }
        
        // If there are multiple blocks, parse them all
        const parsedBlocks = [];
        for (const match of matches) {
            const contentType = match[1];
            const jsonData = match[2].trim();
            
            try {
                const structuredContent = JSON.parse(jsonData);
                parsedBlocks.push({
                    contentType,
                    structuredContent
                });
            } catch (parseError) {
                /* eslint-disable no-console */
                console.error('Failed to parse structured block:', {contentType, error: parseError.message, jsonData: jsonData.substring(0, 100)});
                /* eslint-enable no-console */
                // Skip this block and continue with others
            }
        }
        
        // Remove all structured markers from text
        const cleanText = text.replace(structuredPattern, '').trim();
        
        // Return with contentType set to 'multiple' and all blocks in an array
        return {
            contentType: 'multiple',
            structuredContent: parsedBlocks,
            cleanText,
            rawStructuredText
        };
    } catch (error) {
        // If JSON parse fails, return null and the text will be displayed as-is
        /* eslint-disable no-console */
        console.error('Failed to parse structured content:', error);
        /* eslint-enable no-console */
        return null;
    }
};
